import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Title, 
  Card, 
  Group, 
  TextInput, 
  Textarea, 
  NumberInput, 
  Select, 
  Switch, 
  Button,
  ActionIcon,
  Text,
  LoadingOverlay
} from '@mantine/core';
import { IconArrowLeft, IconRefresh } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useProduct, useUpdateProduct, useCreateProduct } from '../../hooks/use-products';
import { useCacheControl } from '../../hooks/use-cache-control';
import { queryKeys } from '../../lib/query-key-factory';

export function ProductDetailPage() {
  const navigate = useNavigate();

  const {id} = useParams({strict: false});
  console.log(id);
  const isNewProduct = id === 'new';
  const productId = isNewProduct ? null : id;

  
  // Gestione stato del form
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: '',
    inStock: true
  });
  
  // Query per ottenere i dati del prodotto (solo in modalità modifica)
  const { 
    data: product, 
    isLoading, 
    error,
    refetch
  } = useProduct(productId);
  
  // Mutations per creare/aggiornare prodotti
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct(productId);
  
  // Accesso diretto alle funzionalità di controllo della cache
  const { invalidateEntityCache } = useCacheControl();
  
  // Popola il form quando i dati del prodotto vengono caricati
  useEffect(() => {
    if (product) {
      setForm({
        name: product.name,
        description: product.description,
        price: product.price,
        category: product.category,
        inStock: product.inStock
      });
    }
  }, [product]);
  
  const handleInputChange = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Gestione invio form
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isNewProduct) {
      createMutation.mutate(form, {
        onSuccess: () => {
          navigate({ to: '/products' });
        }
      });
    } else {
      updateMutation.mutate(form, {
        onSuccess: () => {
          navigate({ to: '/products' });
        }
      });
    }
  };
  
  // Gestione ricarica manuale
  const handleManualRefresh = () => {
    if (isNewProduct) return;
    
    // Aggiorna solo questo specifico prodotto
    refetch();
    
    // Altro metodo: invalidazione diretta
    // invalidateEntityCache('products');
  };
  
  if (error && !isNewProduct) {
    return (
      <Container size="md" py="xl">
        <Card withBorder p="xl">
          <Text color="red" ta="center">
            Si è verificato un errore nel caricamento del prodotto.
          </Text>
          <Group justify="center" mt="md">
            <Button 
              variant="outline" 
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate({ to: '/products' })}
            >
              Torna all'elenco
            </Button>
            <Button onClick={handleManualRefresh}>Riprova</Button>
          </Group>
        </Card>
      </Container>
    );
  }
  
  return (
    <Container size="md" py="xl">
      <Group justify="apart" mb="lg">
        <Group>
          <ActionIcon
            variant="subtle"
            onClick={() => navigate({ to: '/products' })}
            title="Torna indietro"
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={2}>
            {isNewProduct ? 'Nuovo Prodotto' : `Modifica Prodotto: ${product?.name}`}
          </Title>
        </Group>
        
        {!isNewProduct && (
          <ActionIcon
            variant="light"
            color="blue"
            onClick={handleManualRefresh}
            title="Ricarica dati"
          >
            <IconRefresh size={18} />
          </ActionIcon>
        )}
      </Group>
      
      <Card withBorder p="xl" pos="relative">
        <LoadingOverlay visible={isLoading} />
        
        <form onSubmit={handleSubmit}>
          <TextInput
            label="Nome prodotto"
            placeholder="Inserisci il nome del prodotto"
            required
            value={form.name}
            onChange={(e) => handleInputChange('name', e.currentTarget.value)}
            mb="md"
          />
          
          <Textarea
            label="Descrizione"
            placeholder="Descrizione dettagliata del prodotto"
            minRows={3}
            value={form.description}
            onChange={(e) => handleInputChange('description', e.currentTarget.value)}
            mb="md"
          />
          
          <Group grow mb="md">
            <NumberInput
              label="Prezzo"
              placeholder="0.00"
              required
              decimalScale={2}
              min={0}
              value={form.price}
              onChange={(value) => handleInputChange('price', value)}
            />
            
            <Select
              label="Categoria"
              placeholder="Seleziona una categoria"
              required
              data={[
                { value: 'elettronica', label: 'Elettronica' },
                { value: 'abbigliamento', label: 'Abbigliamento' },
                { value: 'casa', label: 'Casa' },
                { value: 'sport', label: 'Sport' },
              ]}
              value={form.category}
              onChange={(value) => handleInputChange('category', value)}
            />
          </Group>
          
          <Switch
            label="Disponibile in magazzino"
            checked={form.inStock}
            onChange={(e) => handleInputChange('inStock', e.currentTarget.checked)}
            mb="xl"
          />
          
          <Group justify="right">
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/products' })}
            >
              Annulla
            </Button>
            <Button 
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {isNewProduct ? 'Crea Prodotto' : 'Aggiorna Prodotto'}
            </Button>
          </Group>
        </form>
      </Card>
    </Container>
  );
}